"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { BookOpen, CalendarDays, GraduationCap, Users, Video, LayoutDashboard, Wrench, Newspaper } from "lucide-react";

import { FoundingMemberBadge } from "@/components/founding-member-badge";

type WelcomeRole = "student" | "tutor";

const STUDENT_STEPS = [
  { href: "/student", icon: BookOpen, title: "Explore your student dashboard", body: "Open it and click around. Add your cert if you haven’t yet — sessions and study tools live here once you set them up." },
  { href: "/student", icon: CalendarDays, title: "Make a study plan and put it on your calendar", body: "Build a plan, then track it and add events to your calendar right from the app." },
  { href: "/feed", icon: Newspaper, title: "Share a milestone on the feed", body: "Post a study win. Example: Study Journey Started — kicked off AWS Solutions Architect prep." },
  { href: "/study-groups", icon: Users, title: "Join a study group", body: "If you skipped this in setup, join one. If you already joined, say hello." },
];

const TUTOR_STEPS = [
  { href: "/tutor", icon: LayoutDashboard, title: "Open your tutor dashboard", body: "Bookings, students, and earnings land here." },
  { href: "/tutor/setup", icon: Wrench, title: "Finish setup", body: "Connect payouts, set availability, and add Zoom." },
  { href: "/feed", icon: Newspaper, title: "Share a tip on the feed", body: "A short post is how students find you." },
  { href: "/videos", icon: Video, title: "Post a teaching clip", body: "A walkthrough builds trust before anyone books." },
];

export function FoundingWelcomeModal({
  role,
  open,
  onDismiss,
}: {
  role: WelcomeRole;
  open: boolean;
  onDismiss: () => void;
}) {
  const steps = role === "tutor" ? TUTOR_STEPS : STUDENT_STEPS;
  const RoleIcon = role === "tutor" ? GraduationCap : BookOpen;
  const actionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    actionRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="presentation">
      <div className="absolute inset-0 bg-black/55" onClick={onDismiss} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="founding-welcome-title"
        className="relative z-10 w-full max-w-md max-h-[min(90dvh,40rem)] overflow-y-auto rounded-2xl border border-white/15 bg-[#0a0a0a] p-6 text-white shadow-xl"
      >
        <div className="mb-2 flex justify-center">
          <FoundingMemberBadge size="lg" className="ring-white/30" />
        </div>
        <h2 id="founding-welcome-title" className="text-center text-2xl font-bold tracking-tight text-white">
          Welcome, Founding Member
        </h2>
        <div className="mt-3 space-y-3 text-center text-sm text-white/70">
          <p>Thank you for helping us build CertLynx with our first 100 members.</p>
          <p>
            Your Founding Member badge lets the community know you were here
            from the beginning and helped shape CertLynx as it grows.
          </p>
          <p>
            Your feedback, ideas, and participation will help influence what
            we build next.
          </p>
          <p className="font-medium text-white/85">Welcome to the first 100.</p>
        </div>
        <p className="mt-5 text-[11px] font-medium uppercase tracking-[0.18em] text-white/50">
          Start here
        </p>
        <ul className="mt-2 space-y-2">
          {steps.map(({ href, icon: Icon, title, body }) => (
            <li key={href}>
              <Link
                href={href}
                onClick={onDismiss}
                className="flex items-start gap-3 rounded-lg border border-white/15 px-3 py-2.5 hover:bg-white/5"
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-white" />
                <span>
                  <span className="block text-sm font-medium text-white">{title}</span>
                  <span className="block text-xs text-white/55">{body}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <button
          ref={actionRef}
          type="button"
          onClick={onDismiss}
          className="mt-4 h-10 w-full rounded-lg bg-white text-sm font-semibold text-black hover:bg-white/90"
        >
          I&apos;m in
        </button>
        <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-white/40">
          <RoleIcon className="h-3 w-3" />
          {role === "tutor" ? "Founding tutor" : "Founding tester"}
        </p>
      </div>
    </div>
  );
}
